import T from '@/components/Helpers/Translate'
import { Button } from '@/components/_ui/Button'
import { useGetMeReferralCode } from '@/lib/hooks/services/useGetMeReferralCode'
import { copyTextToClipboard } from '@/lib/utils/shared/copyTextToClipboard'
import useGlobalStore from '@/store/useGlobalStore'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { generateCartEventData } from '@/lib/services/mixpanel/helpers/generateCartEventData'

export const CartShareButton = () => {
  const [linkCopied, setLinkCopied] = useState(false)
  const [sharedItems, setSharedItems] = useState('')
  const { isLoadingMeReferralCode, referralCode } = useGetMeReferralCode()
  const { gtm } = useGTM()

  const {
    app: { alpha3, language, marketExceptions },
    user: { isSignedIn, customerType },
    cart,
  } = useGlobalStore()

  const handleClick = () => {
    const currentItems = [...cart.otp, ...cart.subscribe]
    gtm.shareURLCart(currentItems)

    mixpanelService
      .trackEvent(MixpanelEvent.CART_SHARE_LINK_COPIED, {
        cart: generateCartEventData(
          cart,
          marketExceptions?.currencyCode || 'USD',
          customerType,
        ),
      })
      .catch(() => {})

    setLinkCopied(true)
    copyTextToClipboard({
      text: `${window.location.origin}/${alpha3}/${language}/c/${
        isSignedIn && referralCode ? referralCode : 'null'
      }/cart${sharedItems}`,
    })
  }

  useEffect(() => {
    let sharableItems = ''

    for (const item of [...cart.otp, ...cart.subscribe]) {
      sharableItems += `${sharableItems.length > 0 ? ';' : '/'}${item.slug}:${
        item.product.sku
      }:${item.po}:${item.qty}`
    }

    setSharedItems(sharableItems)
  }, [cart])

  if (cart.otp.length <= 0 && cart.subscribe.length <= 0) {
    return null
  }

  return (
    <div className="flex items-center space-x-2">
      <span className="mt-1 text-sm text-secondary">
        <T>share_your_cart_with_a_friend</T>
      </span>
      {!isLoadingMeReferralCode && (
        <Popover onOpenChange={() => setLinkCopied(false)}>
          <PopoverTrigger>
            <Button
              variant="icon"
              className="h-[35px] w-[35px] rounded-full p-2 hover:bg-[#E9EFF5] "
              data-test="product-share-button"
            >
              <Image src="/icons/share.svg" height={16} width={16} alt="Cart" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="mr-[-35px] space-x-2 rounded-lg border bg-white p-3 drop-shadow-sm"
            side="bottom"
            align="end"
            alignOffset={1000}
          >
            <div className="flex flex-row rounded-lg text-sm text-primary">
              <Image
                className="mr-1"
                src="/icons/share.svg"
                height={16}
                width={16}
                alt="Copy link"
              />
              {!linkCopied && (
                <span
                  className="cursor-pointer hover:text-[#5A8FC3]"
                  onClick={handleClick}
                >
                  <T>copy_link</T>
                </span>
              )}
              {linkCopied && (
                <span>
                  <T>link_copied</T>
                </span>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
